<template>
  <div>
    <div class="border-dashboard">
      <div
        class="pt-6 pb-4 border-dashboard-bottom"
        :class="$vuetify.display.smAndDown ? 'mx-6 text-18' : 'mx-6 text-20'"
      >
        Financial wellness for college
      </div>

      <v-row row wrap class="mx-0">
        <v-col cols="12" :md="compact ? 5 : 4" :lg="compact ? 5 : 4" :xl="compact ? 5 : 4" class="pa-0">
          <!-- TODO: Add the coming DATES up section -->
          <!-- <div :class="$vuetify.display.smAndDown ? 'pa-3 ma-3' : 'pa-4 ma-4'" style="background-color: #f8f8f8">
          <div class="font-weight-bold mb-3 text-18"><v-icon class="mr-2">mdi-calendar</v-icon>Coming up</div>
          <div class="py-2 px-3 financial-date">
            <div>
              <div class="text-accelerate-grey-2 caption">MAY 1</div>
              <div class="pt-1 text-body-1">FAFSA priority date</div>
            </div>
            <div>
              <v-icon style="color: #8c1d40">mdi-chevron-right</v-icon>
            </div>
          </div>
          <div class="financial-wellness-link text-body-1 mt-3">See all financial dates</div>
        </div> -->

          <div class="border-dashboard" :class="$vuetify.display.smAndDown ? 'pa-4 ma-4' : 'pa-5 ma-5'">
            <div class="font-weight-bold">
              <img :src="chartLineUpIcon" class="mr-2" />
              Your Estimated Savings
              <v-tooltip
                :right="$vuetify.display.mdAndUp"
                :bottom="$vuetify.display.smAndDown"
                color="Color/Gray/dark-2"
                max-width="280px"
                @update:modelValue="
                  (val) =>
                    val
                      ? asuoDataLayerClick('accelerateDashboard', '15', {
                          section: 'financial wellness for college',
                          text: tooltipMessage.toLowerCase()
                        })
                      : asuoDataLayerClick('accelerateDashboard', '16', {
                          section: 'financial wellness for college',
                          text: tooltipMessage.toLowerCase()
                        })
                "
              >
                <template v-slot:activator="{ props }">
                  <img v-bind="props" class="info-icon" :src="circleInfoRegularIcon" alt="Info" />
                </template>
                <span class="tooltip-text">
                  {{ tooltipMessage }}
                </span>
              </v-tooltip>
            </div>

            <div
              v-if="estimatedSavings.creditsCompleted === 0"
              class="align-center text-accelerate-grey-2 px-4 py-2 text-body-2"
              style="display: flex; background-color: #f6f6f6"
              :class="$vuetify.display.smAndDown ? 'my-4' : 'my-6'"
            >
              <v-icon class="mr-4">mdi-information</v-icon>
              <div>Convert a completed course for credit to start to see your savings</div>
            </div>
            <div v-if="hasEstimatedCost">
              <div class="font-weight-bold savings-amount mt-4 pb-4 border-dashboard-bottom" style="font-size: 30px">
                {{ formattedEstimatedCost }}
              </div>
              <div class="text-body-2 mt-4 text-accelerate-grey-2">
                <div>
                  Verified ASU credits: <span class="font-weight-bold">{{ estimatedSavings.creditsCompleted }}</span>
                </div>
                <div class="mt-1">
                  Typical ASU tuition:
                  <span class="font-weight-bold">{{ formattedEstimatedTuition }}</span>
                  <a
                    href="https://tuition.asu.edu/cost/tuition-estimator"
                    target="_blank"
                    @click="asuoDataLayerClick('accelerateDashboard', '23')"
                  >
                    <v-icon class="ml-2" style="color: #8c1d40">mdi-open-in-new</v-icon>
                  </a>
                </div>
                <div class="mt-1">
                  Your cost so far: <span class="font-weight-bold">${{ estimatedSavings.totalCost }}</span>
                </div>
              </div>
            </div>
          </div>
        </v-col>

        <v-col
          cols="12"
          :md="compact ? 7 : 8"
          :lg="compact ? 7 : 8"
          :xl="compact ? 7 : 8"
          :class="$vuetify.display.smAndDown ? 'pa-4' : 'pa-6 pr-12'"
        >
          <div class="font-weight-bold text-18"><v-icon class="mr-2">mdi-file</v-icon>Essential financial tools</div>
          <div class="d-flex flex-column" style="gap: 16px; margin-top: 16px">
            <div
              v-for="(financialTool, index) in financialTools"
              :key="financialTool.title"
              :class="{
                'border-dashboard-bottom pb-4': index !== financialTools.length - 1
              }"
            >
              <div class="text-body-2 financial-tool">
                <a
                  :href="financialTool.url"
                  @click="handleFinancialToolClick(financialTool.title)"
                  target="_blank"
                  class="financial-wellness-link"
                  :class="{ 'text-subtitle-1': $vuetify.display.mdAndUp }"
                >
                  {{ financialTool.title }}
                </a>
                <div class="text-accelerate-grey-2">
                  {{ financialTool.subtext }}
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useCoursesStore } from '@/stores/courses';
import chartLineUpIcon from '@/assets/icons/chart-line-up.svg';
import circleInfoRegularIcon from '@/assets/icons/circle-info-regular.svg';

import { DataLayerMixin } from '../../mixins/dataLayerMixin';
export default defineComponent({
  mixins: [DataLayerMixin],
  name: 'DashboardFinancialWellnessCard',

  props: {
    compact: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      tooltipMessage:
        'An estimate of your potential degree savings in the Accelerate ASU program based on the typical tuition cost for ASU courses.',
      financialTools: [
        {
          title: 'Scholarship Information and Personalized Recommendations',
          subtext:
            'Information about scholarships and details about ScholarshipUniverse, a tool for personalized opportunities and streamlined applications to maximize funding.',
          url: 'https://tuition.asu.edu/financial-aid/scholarships'
        },
        {
          title: 'Financial Literacy for College Students (iGrad)',
          subtext:
            'Interactive online exercises, modules, articles, and videos for students and parents on saving, budgeting, loans, and additional financial topics.',
          url: 'https://asu.igrad.com/'
        },
        {
          title: 'Understanding Tuition, Financial Aid, and Payment Options',
          subtext: 'Navigate college expenses with tools and resources from ASU.',
          url: 'https://tuition.asu.edu/'
        }
      ],
      chartLineUpIcon,
      circleInfoRegularIcon
    };
  },

  computed: {
    estimatedSavings() {
      const coursesStore = useCoursesStore();
      return coursesStore.estimatedSavings;
    },
    hasEstimatedCost() {
      const { estimatedCost = {} } = this.estimatedSavings || {};
      return estimatedCost.min && estimatedCost.max;
    },
    formattedEstimatedCost() {
      const { estimatedCost = {}, totalCost = 0 } = this.estimatedSavings || {};
      const { min, max } = estimatedCost;

      if (min == null && max == null) return '-';

      const minCost = min != null ? `$${this.toK(min - totalCost)}` : '-';
      const maxCost = max != null ? `$${this.toK(max - totalCost)}` : '-';

      return `${minCost} – ${maxCost}`;
    },
    formattedEstimatedTuition() {
      const { estimatedCost = {} } = this.estimatedSavings || {};
      const { min, max } = estimatedCost;

      if (min == null && max == null) return '-';

      const minTuition = min != null ? `$${this.toK(min)}` : '-';
      const maxTuition = max != null ? `$${this.toK(max)}` : '-';

      return `${minTuition} – ${maxTuition}`;
    }
  },

  methods: {
    toK(value) {
      return value >= 1000 ? (value / 1000).toFixed(1) + 'K' : value.toString();
    },
    handleFinancialToolClick(dataLayerEventText) {
      this.asuoDataLayerClick('accelerateDashboard', '7', { text: dataLayerEventText.toLowerCase() });
    }
  }
});
</script>

<style lang="scss" scoped>
.financial-date {
  background-color: #fff;
  border: 1px solid #d0d0d0;
  border-radius: 5px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.savings-amount {
  color: #498500;
  font-size: 30px;
}

.tooltip-text {
  font-size: 14px;
  color: white;
  text-align: left;
}

.info-icon {
  cursor: pointer;
  font-size: 13px;
}

.financial-wellness-link {
  color: #8c1d40;
  text-decoration: underline;
}

.text-18 {
  font-size: 18px;
}

.text-20 {
  font-size: 20px;
}
</style>
